import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { CommunityGroupDetailsDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Container } from "@/design-system/Container";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Tiles } from "@/design-system/Tiles";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { GroupCard } from "./GroupCard";

type ListProps = {
  type: CommunityGroupDetailsDto["type"];
  followLabels: {
    button: string;
    failedNotification: string;
  };
  unfollowLabels: {
    button: string;
    failedNotification: string;
  };
  allAudienceLabel: string;
  deleteLabels: {
    confirmation: string;
    description: string;
    success: string;
    error: string;
  };
  statusLabels: {
    disable: {
      confirmation: string;
      description: string;
      success: string;
      error: string;
    };
    enable: {
      success: string;
      error: string;
    };
  };
};

export function List(props: ListProps): React.ReactNode {
  const { t } = useTranslation();
  const communityGroupQueries = useCommunityGroupQueries();

  const queryArgs = {
    groupTypes: props.type === "interest" ? ["interest", "realEstate"] : ["helpCategory"],
    includeDisabled: true,
    Limit: 10000,
  } satisfies ApiQueryParams<"getGroupsV2">;
  const { data: groups = [], isLoading, error } = useQuery(communityGroupQueries.list(queryArgs));
  const followMutation = communityGroupMutations.useFollowGroup(queryArgs);
  const unfollowMutation = communityGroupMutations.useUnfollowGroup(queryArgs);
  const deleteGroupMutation = communityGroupMutations.useDeleteGroup();
  const enableGroupMutation = communityGroupMutations.useEnableGroup();
  const disableGroupMutation = communityGroupMutations.useDisableGroup();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("group-delete-modal");
  const [disableGroupId, setDisableGroupId] = useState<string | null>(null);

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <>
      <Container fullWidth>
        <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
          {groups.map((group) => (
            <GroupCard
              key={group.id}
              onEnableGroup={() => enableGroupMutation.mutate({ id: group.id, groupType: group.type })}
              followButtonLabel={props.followLabels.button}
              unfollowButtonLabel={props.unfollowLabels.button}
              onFollow={() => followMutation.mutate({ id: group.id, groupType: group.type })}
              onUnfollow={() => unfollowMutation.mutate({ id: group.id, groupType: group.type })}
              onDisableGroup={setDisableGroupId}
              onDeleteGroup={openDeleteModal}
              {...{ group }}
            />
          ))}
        </Tiles>
      </Container>
      <DeleteModal<string>
        title={props.deleteLabels.confirmation}
        description={props.deleteLabels.description}
        onDelete={(x) => deleteGroupMutation.mutateAsync({ id: x, groupType: props.type })}
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
      <ConfirmModal
        title={props.statusLabels.disable.confirmation}
        description={props.statusLabels.disable.description}
        tone="warning"
        onReject={() => setDisableGroupId(null)}
        onOpenChange={(state) => {
          if (!state) {
            setDisableGroupId(null);
          }
        }}
        onResolve={async () => {
          await disableGroupMutation.mutateAsync({ id: disableGroupId!, groupType: props.type });
          setDisableGroupId(null);
        }}
        resolveBtnProps={{
          text: t("common.action.disable"),
          "data-testid": "group-disable-modal-disable",
        }}
        isOpened={disableGroupId != null}
        shouldCloseOnEsc
        isLoading={disableGroupMutation.isPending}
      />
    </>
  );
}
